import { useCallback } from 'react';
import { getAccSyncJobAPI, syncAccDocsAPI } from '../common/services';
import { errorMessageOf } from '../utils';
import useImportJob, { toCount } from './useImportJob';

const plural = (count) => `${count} document${count === 1 ? '' : 's'}`;

// Unchanged docs still count as synced; only missing and failed ones need a word.
const describe = ({ metadata = {} }) => {
    const checked = toCount(metadata.checkedCount);
    const updated = toCount(metadata.updatedCount);
    const missing = toCount(metadata.missingCount);
    const failed = toCount(metadata.failedCount);
    if (!checked) return { success: true, message: 'There are no Forma documents to sync.' };

    const summary = `Synced ${plural(checked)} from Forma, ${updated} updated`;
    if (!missing && !failed) return { success: true, message: `${summary}.` };

    const failedNames = (metadata.failures || []).map((failure) => failure.displayName).filter(Boolean);
    const detail = failedNames.length ? ` (${failedNames.slice(0, 3).join(', ')}${failedNames.length > 3 ? ', …' : ''})` : '';
    const problems = [missing && `${missing} not found in Forma`, failed && `${failed} failed`].filter(Boolean).join(', ');
    return { success: false, message: `${summary}; ${problems}${detail}.` };
};

// `checkedCount` is the total, known once the docs to sync are listed.
const describeProgress = (job) => {
    const metadata = job?.metadata || {};
    const total = toCount(metadata.checkedCount);
    if (!total) return 'Syncing your documents from Forma... this might take a while';
    const done = ['updatedCount', 'unchangedCount', 'missingCount', 'failedCount'].reduce((sum, key) => sum + toCount(metadata[key]), 0);
    return `Syncing your documents from Forma... ${done} of ${plural(total)} done`;
};

/**
 * Starts and tracks the org-wide Forma (ACC) docs sync job.
 * A sync already running elsewhere is picked up and tracked instead of failing.
 *
 * @param {Function} options.onFinished - ({ success, message, job }) once the job ends, or when it couldn't start
 * @returns {{ isSyncing, startSync, progressText }}
 */
const useAccDocsSync = ({ onFinished } = {}) => {
    const { isImporting, trackImport, job } = useImportJob({
        fetchJob: getAccSyncJobAPI,
        describe,
        failureMessage: "The Forma documents couldn't be synced.",
        maxWaitMs: 30 * 60 * 1000,
        onFinished,
    });

    const startSync = useCallback(async () => {
        try {
            const res = await syncAccDocsAPI();
            trackImport(res?.data?.job);
        } catch (error) {
            const runningJobId = error?.response?.data?.error?.details?.jobId;
            if (runningJobId) trackImport({ id: runningJobId });
            else onFinished && onFinished({ success: false, message: errorMessageOf(error, "Couldn't start the Forma sync.") });
        }
    }, [trackImport, onFinished]);

    return { isSyncing: isImporting, startSync, progressText: describeProgress(job) };
};

export default useAccDocsSync;
